import { getMD5hash } from '../libs/crypto'
import { pick } from 'lodash'
import { parsePhoneNumber } from 'react-phone-number-input'
import { SHIPPING_STATUS_CODES, ADDRESS_TYPE_CODE } from '../common/constants'
import { isUnscheduledPickup } from '../App/ShippingsTracking/TrackingTable/useShippingActions'

const DOWNLOAD_POD_BASE_URL = 'http://pod.cttexpress.com/SPOD.aspx'

const buildPodUrl = (shippingCode, clientCenterCode, destinPostalCode) => {
  const params = new URLSearchParams({
    s: shippingCode,
    cc: clientCenterCode,
    h: getMD5hash(`${shippingCode}${clientCenterCode}${destinPostalCode}`),
  }).toString()

  return `${DOWNLOAD_POD_BASE_URL}?${params}`
}

export const downloadPOD = data => {
  const { shippingCode, clientCenterCode, destinPostalCode } = data
  const podUrl = buildPodUrl(shippingCode, clientCenterCode, destinPostalCode)
  window.open(podUrl, '_blank')
}

export const getIsDownloadPODAllowed = code =>
  code === SHIPPING_STATUS_CODES.delivered

export const getIsUnscheduledPickup = (
  shipping,
  stepDataCode,
  currentStatusCode,
) =>
  isUnscheduledPickup(shipping.info) &&
  stepDataCode === SHIPPING_STATUS_CODES.collected &&
  (currentStatusCode === SHIPPING_STATUS_CODES.manifested || !currentStatusCode)

export const downloadShippingLabel = printShipment => {
  const downloadShipmentLink = document.createElement('a')
  downloadShipmentLink.href = `data:application/pdf;base64,${printShipment.label}`
  downloadShipmentLink.download = `ctt-etiqueta-${printShipment.shipping_code}`
  downloadShipmentLink.click()
}

const formatServiceTypeOptionsForAutocomplete = serviceTypes => {
  if (!Boolean(serviceTypes)) return serviceTypes

  //HACK: We need to create a custom id made from id (code) + name,
  //since the returned list of services-types hasn't unique ids (L)
  return serviceTypes.map(type => ({
    ...type,
    code: `${type.code}-${type.name}`,
    label: `${type.name}`,
    originalCode: type.code,
  }))
}

const removeServiceTypesDuplicates = serviceTypes => {
  return serviceTypes.reduce((acc, serviceType) => {
    if (!acc.find(elem => elem.code === serviceType.code)) {
      return [...acc, serviceType]
    }

    return acc
  }, [])
}

export const getServiceTypeOptions = (userCenters, clientCenterCode) => {
  let serviceTypes = []

  if (!!clientCenterCode) {
    const selectedCenter = userCenters?.find(
      center => center.id === clientCenterCode,
    )

    if (selectedCenter) {
      serviceTypes = selectedCenter.serviceTypes
    }
  } else {
    serviceTypes = removeServiceTypesDuplicates(
      userCenters.flatMap(userCenter => userCenter.serviceTypes),
    )
  }

  return formatServiceTypeOptionsForAutocomplete(serviceTypes)
}

// ADDRESS FORMAT
export const ADDRESS_STREET_KEYS = ['route', 'streetNumber']
export const ADDRESS_COMPLEMENTARY_KEYS = ['stair', 'floor', 'letter']

const translatedKey = (key, t) => t(`noun:${key}`).toLowerCase()

const ITEMS_SEPARATOR = ', '
const KEY_VALUE_SEPARATOR = ' '

const findValueForKey = (str, compKey, t) => {
  // REVIEW: when using translatedKey fn it does not work - ???
  // const translatedKey = translatedKey(compKey, t)
  const translatedKey = t(`noun:${compKey}`).toLowerCase()
  const compKeyRegexp = new RegExp(`(?<=${translatedKey} ).*`)
  const foundValue = str.match(compKeyRegexp)
  return Boolean(foundValue) ? foundValue[0] : null
}

export const stringToAddressData = (str, t) => {
  const splittedStr = str.split(ITEMS_SEPARATOR)
  const address = splittedStr.shift()

  const baseComplementaryData = {}
  for (const key of ADDRESS_COMPLEMENTARY_KEYS) {
    baseComplementaryData[key] = ''
  }

  const addressData = { address, ...baseComplementaryData }

  if (splittedStr?.length === 0) return addressData

  for (const compKey of ADDRESS_COMPLEMENTARY_KEYS) {
    for (const str of splittedStr) {
      const value = findValueForKey(str, compKey, t)
      if (Boolean(value)) addressData[compKey] = value
    }
  }
  return addressData
}

const complementaryAddressDataToString = (data, t) =>
  data.reduce(
    (acc, [currKey, currValue]) =>
      Boolean(currValue)
        ? acc.concat(
            ITEMS_SEPARATOR,
            `${translatedKey(currKey, t)}`,
            KEY_VALUE_SEPARATOR,
            `${currValue}`,
          )
        : acc,
    '',
  )

export const addressDataToString = (data, t) => {
  const streetDataStr = data.route
    ? `${data?.route} ${data?.streetNumber}`
    : data?.autocompleteAddress +
      (Boolean(data.streetNumber) ? ` ${data.streetNumber}` : '')

  const addressComplementaryData = pick(data, ADDRESS_COMPLEMENTARY_KEYS)
  const complementaryDataStr = complementaryAddressDataToString(
    Object.entries(addressComplementaryData),
    t,
  )

  return streetDataStr + complementaryDataStr
}

/**
 * Returns address data with route and streetNumber merged into address.
 * @param data {object}
 * @param data.city {string}
 * @param data.country {string}
 * @param data.postalCode {string}
 * @param data.route {string}
 * @param data.streetNumber {string}
 * @returns {object}
 */
export const fromGPlacesToFormAddressFormat = (data, t) => ({
  address: addressDataToString(data, t),
  country: data.country,
  city: data.city,
  postalCode: data.postalCode,
})

// REVIEW possible name: transformAddress
export const getCompleteAddressFromData = (addressData, intl, t) => {
  let splittedStreet
  let prefix = '+34'
  let phone = addressData?.phone
  {
    if (addressData?.phone?.length && addressData?.phone[0] === '+') {
      let infoPhone = parsePhoneNumber(addressData?.phone)
      phone = infoPhone.nationalNumber
      prefix = '+' + infoPhone.countryCallingCode
    }

    const splittedStr = addressData.address.split(ITEMS_SEPARATOR)
    let routeAndNumber = splittedStr.shift()

    const filteredSplittedStr = splittedStr.filter(str =>
      ADDRESS_COMPLEMENTARY_KEYS.some(key =>
        str.toLowerCase().includes(t(`noun:${key}`).toLowerCase()),
      ),
    )

    const otherData = splittedStr
      .filter(str => !filteredSplittedStr.includes(str))
      .join(ITEMS_SEPARATOR)

    if (otherData) {
      routeAndNumber += ITEMS_SEPARATOR + otherData
    }

    const baseComplementaryData = {}
    for (const key of ADDRESS_COMPLEMENTARY_KEYS) {
      baseComplementaryData[key] = ''
    }

    const addressData2 = { routeAndNumber, ...baseComplementaryData }

    for (const compKey of ADDRESS_COMPLEMENTARY_KEYS) {
      for (const str of filteredSplittedStr) {
        const value = findValueForKey(str, compKey, t)
        if (Boolean(value)) addressData2[compKey] = value
      }
    }

    splittedStreet = addressData2
  }

  const { routeAndNumber, ...complementaryData } = splittedStreet
  const autocompleteAddress = routeAndNumber
  // const autocompleteAddress = routeAndNumber.concat(
  //   ', ',
  //   addressData.postalCode,
  //   ' ',
  //   addressData.city,
  //   ', ',
  //   intl.formatDisplayName(addressData.country, { type: 'region' }),
  // )
  if (addressData?.postalCode) addressData.postalCode.trim()
  return {
    ...addressData,
    phone: phone,
    phonePrefix: prefix,
    route: addressData.route ?? null,
    contactClientCode: addressData?.contactClientCode,
    comments: addressData.comments,
    streetNumber: addressData.streetNumber ?? null,
    ...complementaryData,
    autocompleteAddress,
  }
}

//NOTE: if not found, no restrictions will be applied
const GOOGLE_PLACES_COUNTRY_RESTRICTIONS_ON_ADDRESS_TYPE = {
  [ADDRESS_TYPE_CODE.ORIGIN]: ['es', 'pt', 'ad', 'gi'],
}

export const getGPlacesRestrictionsForAddressType = addressType =>
  GOOGLE_PLACES_COUNTRY_RESTRICTIONS_ON_ADDRESS_TYPE?.[addressType]
